import { useEffect, useState } from 'react';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
    IoSettingsOutline,
    IoLogOutOutline,
    IoChevronDown,
} from 'react-icons/io5';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import BorderBlueButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderBlueButtonV2';
import RequestService from '@Helpers/URLHelpers/RequestService';
import InvitationService from '@Helpers/URLHelpers/InvitationService';
import ErrorHandler from '@Root/ErrorHandler';

import './OptionsDropDown.scss';

export default function OptionsDropDown(props) {

    const [showDot, setShowDot] = useState(false);
    const [anchorMenu, setAnchorMenu] = useState(null);
    const requestService = new RequestService();
    const invitationService = new InvitationService();
    const errorHandler = new ErrorHandler();
    const navigate = useNavigate();
    const location = useLocation();

    const { activeUserId, activeUser } = useReduxActiveUser();

    const addClass = props?.addClass || '';
    const className = 'optionsDropDown';

    const colorList = [ '#2784B9', '#40c47d', '#FDB16C', '#b06060', '#f77474', '#6080b0', '#9a60b0', '#6cb060', '#b0ae60', '#65412b',
        '#cc9963', '#e1bd83', '#60748b', '#744700', '#c8d579', '#5f8fba', '#3e6073', '#8e6dc1', '#473785', '#9158a9', '#7f3f7a',
        '#583e84', '#073763', '#20124d', '#3d124d', '#64113f', '#780448', '#003e23', '#108764', '#1a4c3d', '#c8d77f', '#11888b',
        '#1a95bd', '#0d6c7b', '#25b56e', '#006e3d' ];

    useEffect(() => {
        if (activeUserId) {
            renderData();
        }
    }, [activeUserId]);

    const renderData = async () => {
        try {
            const requestResponse = await requestService.getRequestListForUser(activeUserId);
            const requests = JSON.parse(requestResponse.body);
            const invitationResponse = await invitationService.getInvitationListForUser(activeUserId);
            const invitations = JSON.parse(invitationResponse.body);
            setShowDot(requests?.length > 0 || invitations?.length > 0);
        } catch (err) {
            errorHandler.handleError(err);
        }
    };

    const handleMenuOpen = (event) => {
        setAnchorMenu(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorMenu(null);
    };

    const handleLoginClick = () => {
        navigate('/login');
    };

    const handleLogout = () => {
        handleMenuClose();
        navigate('/logout');
    };

    const getInitials = (value) => {
        if (!value?.trim()) {
            return 'UA';
        }
        const valueTrimmed = value.trim();
        const names = valueTrimmed.split(/\s+/);
        if (names.length > 1) {
            return (
                names[0].charAt(0) +
                names[names.length - 1].charAt(0)
            ).toUpperCase();
        }
        const emailParts = valueTrimmed.split('@');
        return emailParts[0].substring(0, 2).toUpperCase();
    };

    const getAvatarColor = () => {
        const name = activeUser?.fullName || activeUser?.userName || 'U';
        return colorList[
            name.charCodeAt(0) % colorList.length
        ];
    };

    const renderUserAvatar = (size = 40, showBorder = true) => {
        const avatarStyles = {
            width: size,
            height: size,
            bgcolor: `${getAvatarColor()} !important`,
            border: showBorder ? '2px solid #fff' : 'none',
            boxShadow: showBorder
                ? '0 3px 10px rgba(15, 23, 42, 0.12)'
                : 'none',
            fontWeight: 700,
            fontSize: size <= 40 ? '13px' : '17px',
        };

        if (activeUser?.photo) {
            return (
                <Avatar
                    src={activeUser.photo}
                    alt={activeUser?.fullName || 'User'}
                    sx={avatarStyles}
                />
            );
        }

        return (
            <Avatar
                alt={activeUser?.fullName || 'User'}
                sx={avatarStyles}
            >
                {getInitials(
                    activeUser?.fullName ||
                    activeUser?.userName
                )}
            </Avatar>
        );
    };

    const isLoggedIn =
        Object.keys(activeUser || {}).length > 0;

    return (
        <div className={`${className} ${addClass}`}>
            {isLoggedIn ? (
                <button
                    type="button"
                    className={`${className}__profileButton`}
                    onClick={handleMenuOpen}
                    aria-label="Open profile menu"
                    aria-haspopup="true"
                    aria-expanded={Boolean(anchorMenu)}
                >
                    <div className={`${className}__profileButton__avatar`}>
                        {renderUserAvatar(40)}
                    </div>

                    {location?.pathname !== '/' && (
                        <div className={`${className}__profileButton__info`}>
                            <span
                                className={`${className}__profileButton__name`}
                            >
                                {activeUser?.fullName || 'User'}
                            </span>

                            <span
                                className={`${className}__profileButton__username`}
                            >
                                @{activeUser?.userName || 'user'}
                            </span>
                        </div>
                    )}

                    <IoChevronDown
                        className={`${className}__profileButton__chevron ${
                            anchorMenu
                                ? `${className}__profileButton__chevron--open`
                                : ''
                        }`}
                    />

                    {showDot && (
                        <span
                            className={`${className}__notificationDot`}
                            aria-label="You have pending notifications"
                        />
                    )}
                </button>
            ) : (
                <div className={`${className}__login`}>
                    <BorderBlueButtonV2
                        text="Login"
                        onClick={handleLoginClick}
                    />
                </div>
            )}

            <Menu
                open={Boolean(anchorMenu)}
                anchorEl={anchorMenu}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        className: `${className}__menu`,
                    },
                }}
                MenuListProps={{
                    disablePadding: true,
                }}
            >
                <div className={`${className}__menuHeader`}>
                    <div className={`${className}__menuHeader__avatar`}>
                        {renderUserAvatar(54)}
                    </div>

                    <div className={`${className}__menuHeader__content`}>
                        <div className={`${className}__menuHeader__name`}>
                            {activeUser?.fullName || 'User'}
                        </div>

                        <div className={`${className}__menuHeader__username`}>
                            @{activeUser?.userName || 'user'}
                        </div>
                    </div>
                </div>

                <Divider className={`${className}__divider`} />

                <div className={`${className}__menuBody`}>
                    <MenuItem
                        component={NavLink}
                        to="/home/userManagement/settings"
                        className={`${className}__menuItem`}
                        onClick={handleMenuClose}
                    >
                        <span className={`${className}__menuItem__icon`}>
                            <IoSettingsOutline />
                        </span>

                        <span className={`${className}__menuItem__content`}>
                            <span className={`${className}__menuItem__title`}>
                                Settings
                            </span>

                            <span className={`${className}__menuItem__subtitle`}>
                                Manage your account
                            </span>
                        </span>
                    </MenuItem>

                    <Divider className={`${className}__divider`} />

                    <MenuItem
                        className={`${className}__menuItem ${className}__menuItem--logout`}
                        onClick={handleLogout}
                    >
                        <span className={`${className}__menuItem__icon`}>
                            <IoLogOutOutline />
                        </span>

                        <span className={`${className}__menuItem__content`}>
                            <span className={`${className}__menuItem__title`}>
                                Logout
                            </span>

                            <span className={`${className}__menuItem__subtitle`}>
                                Sign out of your account
                            </span>
                        </span>
                    </MenuItem>
                </div>
            </Menu>
        </div>
    );
}
